// shell/Backdrop/Backdrop.tsx — full-viewport background chooser.
//
// Renders the "Deep Signal" composition at the tier resolved from
// usePreferences().prefs.backdrop:
//   'off'          → the constant frame only (void + grain + vignette — still
//                    designed obsidian, not a dead flat rectangle)
//   'low'          → + two static accent-derived glow lobes (painted once,
//                    zero steady-state cost after the entrance bloom)
//   'high'         → same as low for now; slice B layers the lazy WebGL
//                    shimmer canvas between the lobes and the grain
//   'auto'         → high/low picked by resolveTier's capability heuristic
//
// Full-bleed: the default opaque cards occlude it over the reading column;
// glass mode (cardsOverlay OFF) lets it show through on purpose.
import styled from 'styled-components';
import { usePreferences } from '@/hooks';
import { prefersReducedMotion } from './prefersReducedMotion';
import { DeepSignalBackdrop } from './DeepSignalBackdrop';
import { resolveTier } from './resolveTier';

const Fixed = styled.div`
  position: fixed; inset: 0; z-index: 0; overflow: hidden; pointer-events: none;
  background: ${({ theme }) => theme.color.bgRoot};
`;

export function Backdrop() {
  const { prefs } = usePreferences();
  const tier = resolveTier(prefs.backdrop);
  const paused = prefs.reduceMotion || prefersReducedMotion();
  // Shell context: the user's bgIntensity scales the Sundown Signal down from
  // the auth set-piece (which always renders at full 1.0).
  return (
    <Fixed data-testid={tier === 'off' ? 'backdrop-off' : 'backdrop-signal'} aria-hidden>
      <DeepSignalBackdrop tier={tier} paused={paused} intensity={prefs.bgIntensity} />
    </Fixed>
  );
}
